import { FormControl, MenuItem, MenuProps, Select, TextField } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { Dispatch, SetStateAction } from 'react';
import { Text } from 'components/Typography';
import { customDateTimePickerStyles, makeCustomInputStyles, makeCustomSelectStyles } from '../../customStyles';
import styles from './FrequencySelectBlock.scss';

export enum FREQUENCY_SELECT_OPTIONS {
  no_repeat = 'DOES_NOT_REPEAT',
  every_day = 'DAILY',
  every_week = 'WEEKLY',
  every_month = 'MONTHLY'
}

export const FrequencySelectOptions = {
  [FREQUENCY_SELECT_OPTIONS.no_repeat]: "Doesn't repeat",
  [FREQUENCY_SELECT_OPTIONS.every_day]: 'Every day',
  [FREQUENCY_SELECT_OPTIONS.every_week]: 'Every week',
  [FREQUENCY_SELECT_OPTIONS.every_month]: 'Every month'
};

export enum ENDS_SELECT_OPTIONS {
  never = 'NEVER',
  after = 'AFTER_SPECIFIC_OCCURRENCES',
  date = 'ON_SPECIFIC_DATE'
}

export type FrequencyType = {
  repeat: FREQUENCY_SELECT_OPTIONS;
  ends?: ENDS_SELECT_OPTIONS;
  after?: number;
  date?: Date | null;
};

type FrequencySelectProps = {
  frequency: FrequencyType;
  allowedFrequencyTypes?: FREQUENCY_SELECT_OPTIONS[];
  showFrequencyDetails?: boolean;
  onFrequencyChanged?: Dispatch<SetStateAction<FrequencyType>>;
  filterDate?: (date: Date) => boolean;
  disabled?: boolean;
  errors: {
    after?: string;
    date?: string;
  };
};

export const FrequencySelectBlock: React.FC<FrequencySelectProps> = ({
  frequency,
  onFrequencyChanged,
  errors,
  filterDate,
  allowedFrequencyTypes,
  showFrequencyDetails = true,
  disabled = false
}) => {
  const customSelectStyles = makeCustomSelectStyles();
  const customInputStyles = makeCustomInputStyles();
  const menuProps: Partial<MenuProps> = {
    autoFocus: false,
    classes: { paper: customSelectStyles.menuPaper },
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' }
  };

  const flags = useFlags();
  const EndsSelectOptions = {
    ...(flags.allowTaskCreationOccurenceNever && {
      [ENDS_SELECT_OPTIONS.never]: 'Never'
    }),
    [ENDS_SELECT_OPTIONS.after]: 'After',
    [ENDS_SELECT_OPTIONS.date]: 'On a specific date'
  };

  const allowedFrequency = allowedFrequencyTypes?.length
    ? Object.values(FREQUENCY_SELECT_OPTIONS).filter((item) => allowedFrequencyTypes.includes(item))
    : Object.values(FREQUENCY_SELECT_OPTIONS);

  return (
    <div className={styles.container}>
      <Text variant="nlg">Frequency</Text>
      <FormControl fullWidth>
        <Select
          MenuProps={menuProps}
          value={frequency.repeat}
          onChange={(event) => {
            if (!event.target.value) return;
            onFrequencyChanged?.({ repeat: event.target.value as FREQUENCY_SELECT_OPTIONS, ends: ENDS_SELECT_OPTIONS.after, after: 1, date: new Date() });
          }}
          className={customSelectStyles.select}
          disabled={disabled}
        >
          {allowedFrequency.map((option) => (
            <MenuItem value={option} key={option}>
              <Text variant="md">{FrequencySelectOptions[option]}</Text>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {showFrequencyDetails && !disabled && frequency.repeat !== FREQUENCY_SELECT_OPTIONS.no_repeat && (
        <>
          <Text variant="nlg" className={styles.title}>
            Ends
          </Text>
          <FormControl fullWidth>
            <Select
              MenuProps={menuProps}
              value={frequency.ends}
              onChange={(event) => {
                if (!event.target.value) return;
                onFrequencyChanged?.((prev: FrequencyType) => ({
                  repeat: prev.repeat,
                  ends: event.target.value as ENDS_SELECT_OPTIONS,
                  after: 1,
                  date: new Date()
                }));
              }}
              className={customSelectStyles.select}
            >
              {Object.entries(EndsSelectOptions).map(([key, value]) => (
                <MenuItem value={key} key={key}>
                  <Text variant="md">{value}</Text>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}
      {showFrequencyDetails && !disabled && frequency.repeat !== FREQUENCY_SELECT_OPTIONS.no_repeat && frequency.ends === ENDS_SELECT_OPTIONS.after && (
        <>
          <div className={styles.inputWrapper}>
            <TextField
              name="frequencyAfterInput"
              variant="outlined"
              value={frequency.after || 1}
              onChange={(value) => {
                const number = Number(value.target.value);
                if (number > 0 && number <= 99) {
                  onFrequencyChanged?.((prev: FrequencyType) => ({
                    ...prev,
                    after: number,
                    date: undefined
                  }));
                }
              }}
              type="number"
              className={classNames(customInputStyles.input, styles.numberInput)}
              error={!!errors.after}
            />
            <Text variant="md" className={styles.endsInputLabel}>
              Occurrences
            </Text>
          </div>
          {errors.after ? (
            <Text variant="sm" className={styles.error}>
              {errors.after}
            </Text>
          ) : null}
        </>
      )}
      {showFrequencyDetails && !disabled && frequency.ends === ENDS_SELECT_OPTIONS.date && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            value={frequency.date || new Date()}
            onChange={(date) => {
              if (date) {
                onFrequencyChanged?.((prev: FrequencyType) => ({ ...prev, date }));
              }
            }}
            disablePast
            shouldDisableDate={filterDate ? (date) => !filterDate(date as Date) : undefined}
            sx={customDateTimePickerStyles}
          />
        </LocalizationProvider>
      )}
      {!disabled && frequency.ends === ENDS_SELECT_OPTIONS.date && errors.date && (
        <Text variant="sm" className={styles.error}>
          {errors.date}
        </Text>
      )}
    </div>
  );
};
